<template>
  <div class="popup z-index-10-fg confirmation small-popup link-contact-popup">
    <div class="popup-content">
      <div class="popup-header">
        <h2>Link Existing Contact</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('cancel')" />
      </div>
      <div class="popup-body">
        <SearchMenu v-model="searchText" :options="searchResults" placeholder="Search for a company or individual..." @updateContact="handleItemSelect" @forceOptionsUpdate="forceUpdate" />
      </div>
      <div class="popup-footer">
        <button @click="$emit('cancel')" class="text-btn">Cancel</button>
        <button @click="handleLink" class="square-btn" :class="{ saving: isLinking }" :disabled="!selectedItem || isLinking">Link <img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import { computed } from 'vue';
import SearchMenu from './inputs/SearchMenu.vue';
import useSearchWorker from '../composables/useSearchWorker.js';
import useCompaniesQuery from '../composables/useCompaniesQuery.js';
import useContactsQuery from '../composables/useContactsQuery.js';
import useLinkedContactMutate from '../composables/useLinkedContactMutate.js';
import useAlerts from '../composables/useAlerts.js';

export default {
  name: 'LinkExistingContact',
  components: {
    SearchMenu,
  },
  props: {
    companyid: {
      type: String,
      required: true,
    },
    contacts: {
      type: Array,
      required: true,
    },
  },
  emits: ['link', 'cancel', 'success'],
  setup(props) {
    // Initialize the queries
    useCompaniesQuery.useCompaniesQuery();
    useContactsQuery.useContactsQuery();

    // Initialize composables
    const { linkContact, linkCompany } = useLinkedContactMutate();
    const { addAlert } = useAlerts();

    // Use search worker - search both contacts and companies
    const { searchResults, searchText, forceUpdate } = useSearchWorker();

    // Computed property to filter out already linked contacts and companies
    const filteredSearchResults = computed(() => {
      if (!searchResults.value || !props.contacts) {
        return searchResults.value;
      }

      // Get list of contact IDs that are already linked to this company
      const linkedContactIds = props.contacts.filter((contact) => !contact.isCompanyEntry).map((contact) => contact.CONTACTID);

      // Get list of company IDs that are already linked to this company
      const linkedCompanyIds = props.contacts.filter((contact) => contact.isCompanyEntry && contact.company?.COMPANYID).map((contact) => contact.company.COMPANYID);

      // Also exclude the current company itself
      linkedCompanyIds.push(props.companyid);

      // Filter out contacts and companies that are already linked
      return searchResults.value.filter((result) => {
        if (result.type === 'contact') {
          return !linkedContactIds.includes(result.value);
        } else if (result.type === 'company') {
          return !linkedCompanyIds.includes(result.value);
        }
        return true;
      });
    });

    return {
      searchResults: filteredSearchResults,
      searchText,
      forceUpdate,
      linkContact,
      linkCompany,
      addAlert,
    };
  },
  data() {
    return {
      selectedItem: null,
      isLinking: false,
    };
  },
  methods: {
    handleItemSelect(option) {
      if (option && (option.type === 'contact' || option.type === 'company')) {
        this.selectedItem = option;
        this.searchText = option.text;
      } else {
        this.selectedItem = null;
      }
    },
    async handleLink() {
      if (!this.selectedItem || this.isLinking) {
        return;
      }

      this.isLinking = true;

      try {
        if (this.selectedItem.type === 'contact') {
          // Link the contact to the company
          await this.linkContact(this.companyid, this.selectedItem.value);
          this.addAlert('Contact successfully linked to company', 'low', 2);
        } else if (this.selectedItem.type === 'company') {
          // Link the company to the current company
          await this.linkCompany(this.companyid, this.selectedItem.value);
          this.addAlert('Company successfully linked', 'low', 2);
        }

        // Emit success event to notify parent to refresh
        this.$emit('success');
      } catch (error) {
        console.error('Error linking:', error);
        const itemType = this.selectedItem.type === 'contact' ? 'contact' : 'company';
        this.addAlert(`Error linking ${itemType}`, 'high', 5);
      } finally {
        this.isLinking = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.link-contact-popup {
  width: 30rem;
}
</style>
